import { getEnvironmentSettings, ResponseType, Scope, parseTokenDomain, isSessionless, clearSessionless, getTokenStorage, StorageKey, observeImpersonationChanges, getImpersonatedId, Params, TokenType, isLoggingOut, setSessionless, setLastIdp, clearLoggingOut, impersonateUser, setLoggingOut, getUserProfileFromClaims, getImpersonatedUser, storeImpersonatedUser } from '@skyslope/auth-config';
export { Environment, RecoveryFactor, TokenDomain, clearLoggingOut, getEnvironmentSettings, isLoggingOut, parseBaseUrl, parseTokenDomain, validatePassword } from '@skyslope/auth-config';
import { isAccessToken, OktaAuth, toRelativeUrl } from '@okta/okta-auth-js';
import { UserManager } from 'oidc-client';

let environmentSettings;
let features;
const getFeatures = (opts) => {
  var _a;
  return {
    disablePageRefresh: (_a = opts == null ? void 0 : opts.disablePageRefresh) != null ? _a : false
  };
};
const initConfig = (opts) => {
  environmentSettings = getEnvironmentSettings(opts.environment);
  features = getFeatures(opts.features);
};

var __async$5 = (__this, __arguments, generator) => {
  return new Promise((resolve, reject) => {
    var fulfilled = (value) => {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    };
    var rejected = (value) => {
      try {
        step(generator.throw(value));
      } catch (e) {
        reject(e);
      }
    };
    var step = (x) => x.done ? resolve(x.value) : Promise.resolve(x.value).then(fulfilled, rejected);
    step((generator = generator.apply(__this, __arguments)).next());
  });
};
let oktaPromise;
function getOktaUser() {
  return __async$5(this, null, function* () {
    if (oktaPromise)
      return oktaPromise;
    const baseUrl = environmentSettings.okta.baseUrl;
    oktaPromise = fetch(`${baseUrl}/api/v1/users/me`, { credentials: "include" }).then((response) => {
      return response.status === 200 ? response.json() : null;
    }).then(
      (oktaUserResponse) => oktaUserResponse ? {
        id: oktaUserResponse.id,
        email: oktaUserResponse.profile.email,
        firstName: oktaUserResponse.profile.firstName,
        lastName: oktaUserResponse.profile.lastName
      } : null
    ).finally(() => {
      oktaPromise = null;
    });
    return oktaPromise;
  });
}

var __async$4 = (__this, __arguments, generator) => {
  return new Promise((resolve, reject) => {
    var fulfilled = (value) => {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    };
    var rejected = (value) => {
      try {
        step(generator.throw(value));
      } catch (e) {
        reject(e);
      }
    };
    var step = (x) => x.done ? resolve(x.value) : Promise.resolve(x.value).then(fulfilled, rejected);
    step((generator = generator.apply(__this, __arguments)).next());
  });
};
let exchangePromise;
function exchangeToken(token) {
  return __async$4(this, null, function* () {
    if (exchangePromise)
      return exchangePromise;
    exchangePromise = fetch(`${environmentSettings.tokenExchange.baseUrl}/exchange`, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        Authorization: `Bearer ${token}`
      }
    }).then((response) => {
      return response.status === 200 ? response.json() : null;
    }).then((exchangeResponse) => {
      var _a;
      return (_a = exchangeResponse == null ? void 0 : exchangeResponse.access_token) != null ? _a : null;
    }).finally(() => exchangePromise = null);
    return exchangePromise;
  });
}

const LoggerFactory = (devMode, prefix = "@SKYSLOPE-AUTH-JS DEBUG") => {
  return (msg) => {
    if (!devMode)
      return;
    console.log(`${prefix}: ${msg}`);
  };
};

const tokenFragmentKeys = ["t", "token"];
const decodeToken = (oktaAuth, jwt) => {
  try {
    return oktaAuth.token.decode(jwt);
  } catch (ex) {
    return null;
  }
};
const buildOktaAccessToken = (oktaAuth, jwt) => {
  var _a;
  const decoded = decodeToken(oktaAuth, jwt);
  return decoded ? {
    accessToken: jwt,
    issuer: (_a = decoded.payload.iss) != null ? _a : "",
    claims: decoded.payload,
    expiresAt: Number(decoded.payload.exp),
    tokenType: "Bearer",
    scopes: decoded.payload.scp,
    authorizeUrl: `${decoded.payload.iss}/v1/authorize`,
    userinfoUrl: `${decoded.payload.iss}/v1/userinfo`
  } : null;
};
const cleanUrl = (hashFragments) => {
  let cleanUrl2 = `${location.pathname}${location.search}`;
  tokenFragmentKeys.forEach((key) => {
    if (hashFragments.has(key))
      hashFragments.delete(key);
  });
  if (hashFragments.toString()) {
    cleanUrl2 += `#${hashFragments.toString()}`;
  }
  history.replaceState({}, document.title, cleanUrl2);
};
const getTokenFromHashFragment = (oktaAuth) => {
  const hash = location.hash;
  const fragments = hash ? new URLSearchParams(hash.substring(1)) : null;
  const tokenFragment = tokenFragmentKeys.map((k) => fragments == null ? void 0 : fragments.get(k)).find((v) => !!v);
  if (!fragments || !tokenFragment)
    return null;
  cleanUrl(fragments);
  return buildOktaAccessToken(oktaAuth, tokenFragment);
};

var __async$3 = (__this, __arguments, generator) => {
  return new Promise((resolve, reject) => {
    var fulfilled = (value) => {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    };
    var rejected = (value) => {
      try {
        step(generator.throw(value));
      } catch (e) {
        reject(e);
      }
    };
    var step = (x) => x.done ? resolve(x.value) : Promise.resolve(x.value).then(fulfilled, rejected);
    step((generator = generator.apply(__this, __arguments)).next());
  });
};
const DEFAULT_RESPONSE_TYPE = ResponseType.Code;
const DEFAULT_SCOPES = [Scope.OpenId, Scope.Email, Scope.Profile];
class OktaAuthServiceImpl {
  constructor(userManagerOpts, oktaServiceConfig) {
    this._inFlightRefreshes = {};
    this._sessionDetectionPromise = null;
    this.setOriginalUri = (originalUri, state) => this._oktaAuth.setOriginalUri(originalUri, state);
    this.login = (idp, loginHint) => __async$3(this, null, function* () {
      const scopes = this._oktaOptions.scopes || DEFAULT_SCOPES;
      const impersonatedId = getImpersonatedId(environmentSettings);
      const extraParams = {};
      if (impersonatedId) {
        scopes.push(Scope.SkySlopeSupport);
        extraParams[Params.DelegatedUserId] = impersonatedId;
      }
      yield this._oktaAuth.token.getWithRedirect({
        scopes,
        idp: idp != null ? idp : void 0,
        loginHint,
        extraParams
      }).then(() => this._oktaAuth.start()).catch(() => {
      });
    });
    this.frontChannelLogout = () => __async$3(this, null, function* () {
      const idToken = yield this.findTokenByDomain(this._defaultTokenDomain, TokenType.Id);
      this._oktaAuth.stop();
      yield this._oktaAuth.signOut({
        postLogoutRedirectUri: `${window.location.origin}${this._oktaOptions.postLogoutRedirectPath}`,
        idToken
      }).catch(() => {
      });
    });
    this.backChannelLogout = () => __async$3(this, null, function* () {
      this._oktaAuth.stop();
      yield this._oktaAuth.revokeAccessToken().then(() => this._oktaAuth.closeSession()).catch(() => {
      });
    });
    this.getAccessToken = (domain, scopes, force) => __async$3(this, null, function* () {
      var _a, _b, _c;
      let token = null;
      const isAuthenticated = (_a = yield this.getAuthState()) == null ? void 0 : _a.isAuthenticated;
      if (isAuthenticated && !isLoggingOut()) {
        token = yield this.findTokenByDomain(domain, TokenType.Access);
        if (force || !token && !isSessionless()) {
          token = (_c = (_b = yield this.refreshTokens(domain, scopes || this._oktaOptions.scopes || DEFAULT_SCOPES)) == null ? void 0 : _b.accessToken) != null ? _c : null;
        }
      }
      return token;
    });
    this.getAuthState = () => __async$3(this, null, function* () {
      let oktaAuthState = this.oktaAuth.authStateManager.getAuthState();
      if (!oktaAuthState) {
        oktaAuthState = yield this.oktaAuth.authStateManager.updateAuthState();
      }
      return oktaAuthState;
    });
    this.onAuthRequired = () => __async$3(this, null, function* () {
      var _a, _b;
      const authState = yield this.getAuthState();
      if (authState == null ? void 0 : authState.isAuthenticated)
        return;
      const sessionDetected = this._sessionDetectionPromise && (yield this._sessionDetectionPromise);
      if (!sessionDetected) {
        return (_b = (_a = this._oktaServiceConfig) == null ? void 0 : _a.onAuthRequired) == null ? void 0 : _b.call(_a);
      }
    });
    this.findTokenByDomain = (domain, type) => __async$3(this, null, function* () {
      const tokenStorage = this.getTokenStorage(domain, type);
      const existingToken = yield this.oktaAuth.tokenManager.get(tokenStorage);
      return existingToken != null ? existingToken : null;
    });
    this.fetchTokensByDomain = (domain, scopes = void 0) => __async$3(this, null, function* () {
      var _a, _b;
      const reqScopes = scopes || this._oktaOptions.scopes || DEFAULT_SCOPES;
      const impersonatedId = getImpersonatedId(environmentSettings);
      const extraParams = {};
      if (impersonatedId) {
        reqScopes.push(Scope.SkySlopeSupport);
        extraParams[Params.DelegatedUserId] = impersonatedId;
      }
      const authServer = environmentSettings.okta.servers[domain];
      const response = yield (_a = this._oktaAuth.token) == null ? void 0 : _a.getWithoutPrompt({
        scopes: reqScopes,
        responseType: DEFAULT_RESPONSE_TYPE,
        issuer: `${environmentSettings.okta.baseUrl}/oauth2/${authServer}`,
        extraParams
      }).catch((err) => {
        this._logger(err);
        return null;
      });
      return (_b = response == null ? void 0 : response.tokens) != null ? _b : null;
    });
    this.refreshTokens = (domain, scopes) => __async$3(this, null, function* () {
      const inFlightRefresh = this._inFlightRefreshes[domain];
      if (inFlightRefresh) {
        return inFlightRefresh;
      }
      try {
        const tokenPromise = this.fetchTokensByDomain(domain, scopes);
        this._inFlightRefreshes[domain] = tokenPromise;
        const tokens = yield tokenPromise;
        if (tokens == null ? void 0 : tokens.accessToken) {
          this._oktaAuth.tokenManager.add(this.getTokenStorage(domain, TokenType.Access), tokens.accessToken);
        }
        if (tokens == null ? void 0 : tokens.idToken) {
          this._oktaAuth.tokenManager.add(this.getTokenStorage(domain, TokenType.Id), tokens.idToken);
        }
        return tokens;
      } finally {
        delete this._inFlightRefreshes[domain];
      }
    });
    this.transformAuthState = (_, authState) => __async$3(this, null, function* () {
      var _a;
      (_a = this._transformAuthStatePromise) != null ? _a : this._transformAuthStatePromise = this._transformAuthState(_, authState).finally(
        () => this._transformAuthStatePromise = null
      );
      return this._transformAuthStatePromise;
    });
    this._transformAuthStatePromise = null;
    this._transformAuthState = (_, authState) => __async$3(this, null, function* () {
      var _a, _b, _c;
      let accessToken = (_a = getTokenFromHashFragment(this._oktaAuth)) != null ? _a : void 0;
      if (accessToken || isSessionless()) {
        if (accessToken) {
          setSessionless();
          if (accessToken.issuer.indexOf(environmentSettings.okta.servers.external) >= 0) {
            const exchangedJwt = yield exchangeToken(accessToken.accessToken);
            if (exchangedJwt) {
              accessToken = (_b = buildOktaAccessToken(this._oktaAuth, exchangedJwt)) != null ? _b : accessToken;
            }
          }
          yield this.clearTokens();
          this._oktaAuth.tokenManager.add(this.getTokenStorage(this._defaultTokenDomain, TokenType.Access), accessToken);
          authState.accessToken = accessToken;
        }
        authState.isAuthenticated = true;
      } else {
        if (authState.isAuthenticated) {
          authState.idToken = yield this.findTokenByDomain(this._defaultTokenDomain, TokenType.Id);
          authState.accessToken = yield this.findTokenByDomain(
            this._defaultTokenDomain,
            TokenType.Access
          );
        } else if (!isLoggingOut()) {
          if (!authState.isAuthenticated) {
            (_c = this._sessionDetectionPromise) != null ? _c : this._sessionDetectionPromise = this.bootstrapFromAvailableSession();
          }
          if (this._sessionDetectionPromise) {
            const tokens = yield this._sessionDetectionPromise;
            if (tokens) {
              authState.isAuthenticated = true;
              authState.accessToken = tokens.accessToken;
              authState.idToken = tokens.idToken;
            }
          }
        }
      }
      return authState;
    });
    this.onTokenExpired = (_, expiredToken) => {
      if (!isAccessToken(expiredToken))
        return;
      const tokenDomain = parseTokenDomain(expiredToken.authorizeUrl, environmentSettings.okta.servers);
      if (tokenDomain === null) {
        throw new Error("Unable to parse authorization server ID.");
      }
      this.refreshTokens(tokenDomain, expiredToken.scopes).then((tokens) => {
        var _a, _b;
        if (tokens === null) {
          if (!isSessionless()) {
            (_b = (_a = this._oktaServiceConfig) == null ? void 0 : _a.onSessionExpired) == null ? void 0 : _b.call(_a);
          }
        } else {
          clearSessionless();
        }
      });
    };
    this.bootstrapFromAvailableSession = () => __async$3(this, null, function* () {
      var _a, _b;
      const tokens = yield this.refreshTokens(this._defaultTokenDomain, this._oktaOptions.scopes);
      if (tokens === null) {
        return null;
      }
      const originalUri = this._oktaAuth.getOriginalUri();
      if (originalUri) {
        this._oktaAuth.removeOriginalUri();
        yield (_b = (_a = this._oktaServiceConfig) == null ? void 0 : _a.restoreOriginalUri) == null ? void 0 : _b.call(_a, this._oktaAuth, originalUri);
      }
      return tokens;
    });
    this.verifyActiveSession = () => __async$3(this, null, function* () {
      var _a, _b, _c;
      const authState = yield this.getAuthState();
      if ((authState == null ? void 0 : authState.isAuthenticated) && !isSessionless()) {
        const activeUser = yield getOktaUser();
        const impersonatedUserId = getImpersonatedId(environmentSettings);
        const identityClaims = (_a = authState == null ? void 0 : authState.idToken) == null ? void 0 : _a.claims;
        if (activeUser === null) {
          return (_c = (_b = this._oktaServiceConfig) == null ? void 0 : _b.onSessionExpired) == null ? void 0 : _c.call(_b);
        } else if ((identityClaims == null ? void 0 : identityClaims.email) !== activeUser.email || ((identityClaims == null ? void 0 : identityClaims.delegated_user_id) || impersonatedUserId) && (identityClaims == null ? void 0 : identityClaims.delegated_user_id) !== impersonatedUserId) {
          return this.refreshSession();
        }
      }
    });
    this.clearTokens = () => __async$3(this, null, function* () {
      this._oktaAuth.tokenManager.clear();
    });
    this.refreshSession = () => __async$3(this, null, function* () {
      yield this.clearTokens();
      if (!features.disablePageRefresh) {
        window.location.reload();
        return;
      }
      yield this.refreshTokens(this._defaultTokenDomain, this._oktaOptions.scopes);
    });
    this.getTokenStorage = (domain, type) => domain === this._defaultTokenDomain ? type : getTokenStorage(domain, type);
    var _a, _b;
    const oktaOpts = userManagerOpts.providers.okta;
    const authServer = environmentSettings.okta.servers[userManagerOpts.tokenDomain];
    const isLocal = window.location.host.includes("localhost");
    const oktaAuth = new OktaAuth({
      issuer: `${environmentSettings.okta.baseUrl}/oauth2/${authServer}`,
      clientId: oktaOpts.clientId,
      redirectUri: `${window.location.origin}${oktaOpts.redirectPath}`,
      responseType: DEFAULT_RESPONSE_TYPE,
      scopes: oktaOpts.scopes || DEFAULT_SCOPES,
      tokenManager: {
        autoRenew: false,
        autoRemove: false,
        storage: "sessionStorage",
        storageKey: StorageKey.Token,
        expireEarlySeconds: (_a = oktaOpts.expireEarlySeconds) != null ? _a : 180
      },
      cookies: {
        secure: !isLocal
      },
      devMode: (_b = oktaOpts.devMode) != null ? _b : isLocal,
      maxClockSkew: 300,
      transformAuthState: this.transformAuthState
    });
    this._oktaServiceConfig = oktaServiceConfig;
    oktaAuth.authStateManager.subscribe((oktaAuthState) => __async$3(this, null, function* () {
      var _a2;
      if (oktaAuthState.isAuthenticated && oktaAuthState.idToken) {
        setLastIdp(oktaAuthState.idToken.claims.idp);
      }
      return (_a2 = oktaServiceConfig == null ? void 0 : oktaServiceConfig.onAuthStateChanged) == null ? void 0 : _a2.call(oktaServiceConfig, oktaAuthState);
    }));
    oktaAuth.tokenManager.on("expired", this.onTokenExpired);
    document.addEventListener("visibilitychange", () => __async$3(this, null, function* () {
      if (document.visibilityState === "visible") {
        yield this.verifyActiveSession();
      }
    }));
    window.addEventListener("load", () => __async$3(this, null, function* () {
      return this.verifyActiveSession();
    }));
    observeImpersonationChanges(environmentSettings, (value) => __async$3(this, null, function* () {
      var _a2;
      const authState = yield this.getAuthState();
      if (((_a2 = authState == null ? void 0 : authState.accessToken) == null ? void 0 : _a2.claims.delegated_user_id) !== value) {
        this.refreshSession();
      }
    }));
    this._oktaAuth = oktaAuth;
    this._oktaOptions = oktaOpts;
    this._defaultTokenDomain = userManagerOpts.tokenDomain;
    this._logger = LoggerFactory(this._oktaOptions.devMode);
  }
  get oktaAuth() {
    return this._oktaAuth;
  }
}

var __async$2 = (__this, __arguments, generator) => {
  return new Promise((resolve, reject) => {
    var fulfilled = (value) => {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    };
    var rejected = (value) => {
      try {
        step(generator.throw(value));
      } catch (e) {
        reject(e);
      }
    };
    var step = (x) => x.done ? resolve(x.value) : Promise.resolve(x.value).then(fulfilled, rejected);
    step((generator = generator.apply(__this, __arguments)).next());
  });
};
class PrimeAuthServiceImpl {
  constructor(primeAuthOpts, sessionConfig) {
    this.isAuthenticated = () => __async$2(this, null, function* () {
      const user = yield this._userManager.getUser();
      return !!user;
    });
    this.startBackgroundLogin = () => __async$2(this, null, function* () {
      const sessionConfig = this._sessionConfig;
      const userManager = this._userManager;
      return userManager.signinSilent().then((user) => {
        (sessionConfig == null ? void 0 : sessionConfig.onUserSignedOut) && userManager.events.addUserSignedOut(sessionConfig.onUserSignedOut);
        (sessionConfig == null ? void 0 : sessionConfig.onSessionTerminated) && userManager.events.addUserUnloaded(sessionConfig.onSessionTerminated);
        return user;
      }).catch(() => {
      });
    });
    this.endBackgroundLogin = () => __async$2(this, null, function* () {
      return this._userManager.signinSilentCallback().catch(() => {
      });
    });
    this.getAccessToken = () => __async$2(this, null, function* () {
      return this._primeAuthOptions.fetchAccessToken ? this._userManager.getUser().then((user) => {
        var _a;
        return (_a = user == null ? void 0 : user.access_token) != null ? _a : null;
      }) : null;
    });
    this.signoutRedirect = () => __async$2(this, null, function* () {
      return this._userManager.signoutRedirect();
    });
    this.signoutRedirectCallback = () => __async$2(this, null, function* () {
      return this._userManager.signoutRedirectCallback();
    });
    this.getSessionStatus = () => __async$2(this, null, function* () {
      let sessionStatus = null;
      yield this._userManager.querySessionStatus().then((session) => {
        sessionStatus = session;
      }).catch(() => {
      });
      return sessionStatus;
    });
    var _a;
    this._sessionConfig = sessionConfig;
    this._primeAuthOptions = primeAuthOpts;
    this._userManager = new UserManager({
      authority: `${environmentSettings.primeAuth.baseUrl}/identity`,
      client_id: primeAuthOpts.clientId,
      redirect_uri: `${window.location.origin}${primeAuthOpts.redirectPath}`,
      silent_redirect_uri: `${window.location.origin}${primeAuthOpts.redirectPath}`,
      post_logout_redirect_uri: `${window.location.origin}${primeAuthOpts.postLogoutRedirectPath}`,
      response_type: primeAuthOpts.fetchAccessToken ? ResponseType.IdTokenToken : ResponseType.IdToken,
      scope: ((_a = primeAuthOpts.scopes) != null ? _a : [Scope.OpenId, Scope.Email]).join(" "),
      loadUserInfo: false,
      revokeAccessTokenOnSignout: true,
      automaticSilentRenew: true,
      filterProtocolClaims: true
    });
  }
}

var __async$1 = (__this, __arguments, generator) => {
  return new Promise((resolve, reject) => {
    var fulfilled = (value) => {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    };
    var rejected = (value) => {
      try {
        step(generator.throw(value));
      } catch (e) {
        reject(e);
      }
    };
    var step = (x) => x.done ? resolve(x.value) : Promise.resolve(x.value).then(fulfilled, rejected);
    step((generator = generator.apply(__this, __arguments)).next());
  });
};
let accountsPromise;
function getAccountsUser(token) {
  return __async$1(this, null, function* () {
    if (accountsPromise)
      return accountsPromise;
    const baseUrl = environmentSettings.accountsApi.baseUrl;
    accountsPromise = fetch(`${baseUrl}/v3/users/me`, {
      method: "GET",
      headers: [[`Authorization`, `Bearer ${token}`]]
    }).then((response) => {
      if (response.status !== 200) {
        response.text().then((text) => {
          throw new Error(text);
        });
      }
      return response.json();
    }).then((accountsUserResponse) => {
      var _a, _b, _c;
      const attributes = /* @__PURE__ */ new Map();
      accountsUserResponse.result.metadata.forEach(
        (attr) => Object.keys(attr).forEach((key) => attributes.set(key, attr[key]))
      );
      const subscriberId = attributes.get("SubscriberId");
      const primeUserId = attributes.get("PrimeUserId");
      const digisignUserId = attributes.get("DigiSignUserId");
      const profile = {
        id: accountsUserResponse.result.userId,
        email: (_a = attributes.get("Email")) != null ? _a : "",
        firstName: (_b = attributes.get("FirstName")) != null ? _b : "",
        middleName: attributes.get("MiddleName"),
        lastName: (_c = attributes.get("LastName")) != null ? _c : "",
        primaryPhone: attributes.get("PrimaryPhone"),
        nrdsId: attributes.get("NrdsId"),
        loginId: attributes.get("LoginId"),
        oktaGroups: accountsUserResponse.result.oktaGroups
      };
      if (primeUserId || subscriberId) {
        profile.prime = {
          userId: primeUserId,
          subscriberId: subscriberId ? parseInt(subscriberId) : void 0
        };
      }
      if (digisignUserId) {
        profile.digisign = {
          userId: digisignUserId
        };
      }
      return profile;
    }).catch(() => null).finally(() => accountsPromise = null);
    return accountsPromise;
  });
}

var __async = (__this, __arguments, generator) => {
  return new Promise((resolve, reject) => {
    var fulfilled = (value) => {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    };
    var rejected = (value) => {
      try {
        step(generator.throw(value));
      } catch (e) {
        reject(e);
      }
    };
    var step = (x) => x.done ? resolve(x.value) : Promise.resolve(x.value).then(fulfilled, rejected);
    step((generator = generator.apply(__this, __arguments)).next());
  });
};
class UserManagerImpl {
  constructor(opts) {
    this.login = (opts) => __async(this, null, function* () {
      var _a, _b, _c, _d, _e, _f;
      clearLoggingOut();
      this._oktaAuthService.setOriginalUri((_a = opts == null ? void 0 : opts.originalUri) != null ? _a : window.location.href);
      if (((_b = opts == null ? void 0 : opts.provider) == null ? void 0 : _b.uri) || ((_c = opts == null ? void 0 : opts.provider) == null ? void 0 : _c.path)) {
        window.location.assign((_d = opts.provider.uri) != null ? _d : `${window.location.origin}${opts.provider.path}`);
        return;
      }
      yield this._oktaAuthService.login((_f = (_e = opts == null ? void 0 : opts.provider) == null ? void 0 : _e.idp) != null ? _f : opts == null ? void 0 : opts.idp, opts == null ? void 0 : opts.loginHint);
    });
    this.startLogout = (force = false) => __async(this, null, function* () {
      var _a;
      if (getImpersonatedId(environmentSettings)) {
        impersonateUser(environmentSettings, null);
        if (!force)
          return;
      }
      setLoggingOut();
      const isAuthenticated = (_a = this._primeAuthService) == null ? void 0 : _a.isAuthenticated;
      if (isAuthenticated && (yield isAuthenticated())) {
        yield this._oktaAuthService.backChannelLogout().then(() => {
          var _a2;
          return (_a2 = this._primeAuthService) == null ? void 0 : _a2.signoutRedirect();
        });
      } else {
        yield this._oktaAuthService.frontChannelLogout();
      }
      sessionStorage.clear();
    });
    this.endLogout = () => __async(this, null, function* () {
      var _a, _b;
      const isAuthenticated = (_a = this._primeAuthService) == null ? void 0 : _a.isAuthenticated;
      if (isAuthenticated && (yield isAuthenticated())) {
        yield (_b = this._primeAuthService) == null ? void 0 : _b.signoutRedirectCallback();
      }
    });
    this.getAccessToken = (type, scopes, force) => __async(this, null, function* () {
      var _a;
      const defaultTokenDomain = this._userManagerOptions.tokenDomain;
      const tokenDomain = type != null ? type : defaultTokenDomain;
      const tokenScopes = scopes != null ? scopes : tokenDomain === defaultTokenDomain ? this._userManagerOptions.providers.okta.scopes : void 0;
      const accessToken = yield this._oktaAuthService.getAccessToken(tokenDomain, tokenScopes, force);
      return (_a = accessToken == null ? void 0 : accessToken.accessToken) != null ? _a : "";
    });
    this.getUser = () => __async(this, null, function* () {
      const authState = yield this._oktaAuthService.getAuthState();
      if (!(authState == null ? void 0 : authState.isAuthenticated) || !(authState == null ? void 0 : authState.idToken)) {
        return null;
      }
      const claims = authState.idToken.claims;
      const claimsProfile = getUserProfileFromClaims(claims);
      if (!claimsProfile.delegatedUserId) {
        return claimsProfile;
      }
      let storedUser = getImpersonatedUser();
      if (!storedUser || storedUser.id !== claimsProfile.delegatedUserId) {
        const token = yield this.getAccessToken();
        const accountsUser = yield getAccountsUser(token);
        storeImpersonatedUser(accountsUser);
        storedUser = accountsUser;
      }
      let userContext = storedUser;
      if (userContext) {
        userContext.impersonatingUser = claimsProfile;
      } else {
        userContext = claimsProfile;
      }
      return userContext;
    });
    this.impersonateUser = (id) => __async(this, null, function* () {
      return impersonateUser(environmentSettings, id);
    });
    this.forgotPassword = (opts) => __async(this, null, function* () {
      const forgotPasswordStatus = "RECOVERY_CHALLENGE";
      return this._oktaAuthService.oktaAuth.forgotPassword(opts).then((transaction) => {
        return transaction && transaction.status === forgotPasswordStatus;
      }).catch((err) => {
        console.log(`forgot password failed for ${opts.username} - ${err.message}`);
        return false;
      });
    });
    this.getAuthState = () => __async(this, null, function* () {
      var _a;
      const oktaAuthState = yield this._oktaAuthService.getAuthState();
      return {
        isAuthenticated: (_a = oktaAuthState == null ? void 0 : oktaAuthState.isAuthenticated) != null ? _a : false,
        idToken: oktaAuthState == null ? void 0 : oktaAuthState.idToken,
        accessToken: oktaAuthState == null ? void 0 : oktaAuthState.accessToken,
        error: oktaAuthState == null ? void 0 : oktaAuthState.error
      };
    });
    this.getImpersonatedUser = () => __async(this, null, function* () {
      var _a;
      const authState = yield this.getAuthState();
      return (_a = authState.accessToken) == null ? void 0 : _a.claims.delegated_user_id;
    });
    var _a;
    this._userManagerOptions = opts;
    initConfig(opts);
    const oktaServiceConfig = {
      onAuthRequired: () => __async(this, null, function* () {
        var _a2;
        return (_a2 = opts.onAuthRequired) == null ? void 0 : _a2.call(opts, this);
      }),
      onSessionExpired: () => __async(this, null, function* () {
        return yield this.startLogout(true);
      }),
      restoreOriginalUri: (_a = opts.restoreOriginalUri) != null ? _a : (_, originalUri) => __async(this, null, function* () {
        window.location.replace(toRelativeUrl(originalUri != null ? originalUri : "/", window.location.origin));
      })
    };
    this._oktaAuthService = new OktaAuthServiceImpl(opts, oktaServiceConfig);
    if (opts.providers.primeAuth) {
      const primeAuthSessionConfig = {
        onUserSignedOut: () => __async(this, null, function* () {
          return yield this.startLogout(true);
        })
      };
      this._primeAuthService = new PrimeAuthServiceImpl(opts.providers.primeAuth, primeAuthSessionConfig);
    }
  }
  get options() {
    return this._userManagerOptions;
  }
  get idps() {
    return environmentSettings.okta.idps;
  }
  get primeAuth() {
    return this._primeAuthService;
  }
  get _oktaAuth() {
    return this._oktaAuthService;
  }
}
function createUserManager(options) {
  return new UserManagerImpl(options);
}

export { createUserManager };
